import {
  faPlus,
  faTrashCan,
  faInfoCircle,
} from '@fortawesome/pro-regular-svg-icons'
import {
  Box,
  Button,
  Text,
  Tooltip,
  NumberInput,
  Flex,
  type BoxProps,
} from '@mantine/core'
import { useMemo, useState, useEffect } from 'react'

import { useTranslation } from '@flecto/translations'
import {
  type CoreItemPriceRule,
  type CoreItemPriceRuleDurationUnit,
  type CoreItem,
  type UNSAFE_any,
} from '@flecto/types'
import { Icon, InputCurrency } from '@flecto/ui'
import useMediaQuery from '@flecto/ui/lib/utils/useMediaQuery'

import { useTaxesInfo } from '../../../taxes'
import { SelectTypeDurationUnit } from './SelectTypeDurationUnit'
import styles from './styles.module.css'

export type Errors = {
  [key: string]: string
}

export type TableItemPricingRulesProps = BoxProps & {
  item: CoreItem
  onChange: (rules: CoreItemPriceRule[]) => void
  forcePricesWithTaxes?: boolean
  errors?: Errors
}

export function TableItemPricingRules({
  item,
  onChange,
  forcePricesWithTaxes,
  errors,
  ...props
}: TableItemPricingRulesProps) {
  const [localPricingRules, setLocalPricingRules] = useState<
    CoreItemPriceRule[]
  >([])

  const [previousTaxRate, setPreviousTaxRate] = useState<string | null>(null)
  const [previousTaxable, setPreviousTaxable] = useState<boolean | null>(null)

  const { isMobile } = useMediaQuery()

  const { t } = useTranslation('saas', { keyPrefix: 'items' })
  const { t: tActions } = useTranslation('shared', { keyPrefix: 'actions' })
  const { userSettingShowPricesWithTaxes, getCalculatedAmounts } =
    useTaxesInfo()

  const showPricesWithTaxes =
    typeof forcePricesWithTaxes === 'boolean'
      ? forcePricesWithTaxes
      : userSettingShowPricesWithTaxes

  const handleAddRow = () => {
    const amounts = getCalculatedAmounts(
      1000000,
      item.tax_rate,
      item.taxable,
      showPricesWithTaxes,
      forcePricesWithTaxes
    )

    const newRule: CoreItemPriceRule = {
      duration_unit: 'days',
      duration_value: 1,
      price_cents: amounts.totalWithoutTaxes.cents,
    }

    const newRules = [...localPricingRules, newRule]
    setLocalPricingRules(newRules)
    onChange(newRules)
  }

  const handleDeleteRow = (index: number): void => {
    const updatedRules: CoreItemPriceRule[] = localPricingRules.filter(
      (_: CoreItemPriceRule, i: number) => i !== index
    )
    setLocalPricingRules(updatedRules)
    onChange(updatedRules)
  }

  const handleChangeDurationValue = (index: number, value: number) => {
    localPricingRules[index].duration_value = value
    const updatedRules = [...localPricingRules]

    setLocalPricingRules(updatedRules)
    onChange(updatedRules)
  }

  const handleChangeDurationUnit = (
    index: number,
    type: CoreItemPriceRuleDurationUnit
  ) => {
    localPricingRules[index].duration_unit = type

    const updatedRules = [...localPricingRules]

    setLocalPricingRules(updatedRules)
    onChange(updatedRules)
  }

  const handleChangePriceWithoutTaxes = (index: number, cents: number) => {
    localPricingRules[index].price_cents = cents

    const udpatedRules = [...localPricingRules]
    setLocalPricingRules(udpatedRules)
    onChange(udpatedRules)
  }

  const handleChangePriceWithTaxes = (index: number, cents: number) => {
    const amounts = getCalculatedAmounts(
      cents,
      item.tax_rate,
      item.taxable,
      true,
      forcePricesWithTaxes
    )

    localPricingRules[index].price_cents = amounts.totalWithoutTaxes.cents

    const udpatedRules = [...localPricingRules]
    setLocalPricingRules(udpatedRules)
    onChange(udpatedRules)
  }

  useEffect(() => {
    if (item.pricing_rules) {
      setLocalPricingRules(item.pricing_rules)
    }
  }, [item.pricing_rules])

  useEffect(() => {
    if (typeof previousTaxable !== 'boolean') {
      setPreviousTaxable(item.taxable)
      return
    }

    if (previousTaxable !== item.taxable && showPricesWithTaxes) {
      const rules = item.pricing_rules.map((rule) => {
        const previousAmounts = getCalculatedAmounts(
          rule.price_cents,
          item.tax_rate,
          previousTaxable
        )

        const amounts = getCalculatedAmounts(
          previousAmounts.totalWithTaxes.cents,
          item.tax_rate,
          item.taxable,
          true,
          forcePricesWithTaxes
        )

        return {
          ...rule,
          price_cents: !item.taxable
            ? previousAmounts.totalWithTaxes.cents
            : amounts.totalWithoutTaxes.cents,
        }
      })

      setLocalPricingRules(rules)
      onChange(rules)
    }

    setPreviousTaxable(item.taxable)
  }, [item.taxable])

  useEffect(() => {
    if (
      typeof previousTaxRate !== 'string' &&
      typeof item.tax_rate === 'string'
    ) {
      setPreviousTaxRate(item.tax_rate)
      return
    }

    if (showPricesWithTaxes && item.taxable && previousTaxRate) {
      const rules = item.pricing_rules?.map((rule) => {
        const previousAmounts = getCalculatedAmounts(
          rule.price_cents,
          previousTaxRate,
          item.taxable
        )

        const amounts = getCalculatedAmounts(
          previousAmounts.totalWithTaxes.cents,
          item.tax_rate,
          item.taxable,
          true,
          forcePricesWithTaxes
        )

        return {
          ...rule,
          price_cents: amounts.totalWithoutTaxes.cents,
        }
      })

      setLocalPricingRules(rules)
      onChange(rules)
    }

    setPreviousTaxRate(item.tax_rate)
  }, [item.tax_rate, showPricesWithTaxes])

  const errorMessage =
    errors?.pricing_rules_unique || errors?.pricing_rules
      ? t('price.error_unique_message')
      : ''

  const errorValidation = useMemo(() => {
    if (errors) {
      const errorPricingRules = Object.entries(errors)
        .filter(([key]) => key.includes('pricing_rules'))
        .reduce(
          (acc, [key, value]) => {
            const index = key.split('.')[1]
            const field = key.split('.')[2]

            if (!acc[index]) {
              acc[index] = {}
            }

            acc[index][field] = value

            return acc
          },
          {} as Record<string, UNSAFE_any>
        )

      return errorPricingRules
    }
    return undefined
  }, [errors])

  useEffect(() => {
    item.pricing_rules.sort((a, b) => {
      const convertToHours = (unit: string, value: number) => {
        switch (unit) {
          case 'days':
            return value * 24
          case 'hours':
            return value
          default:
            return 0
        }
      }
      const aHours = convertToHours(a.duration_unit, a.duration_value)
      const bHours = convertToHours(b.duration_unit, b.duration_value)
      return aHours - bHours
    })
  }, [])

  return (
    <Box {...props}>
      <Box>
        {localPricingRules?.map((rule, index) => {
          const amounts = getCalculatedAmounts(
            rule.price_cents,
            item.tax_rate,
            item.taxable
          )

          const lineErrors = {
            unitValue: errorValidation?.[index]?.duration_value ?? undefined,
            priceCents: errorValidation?.[index]?.price_cents ?? undefined,
          }

          return (
            <Box key={index} className={styles.linePriceRule}>
              <NumberInput
                data-testid={`input-unit-value-${index}`}
                size='md'
                label={index === 0 || isMobile ? t('price.price_up_to') : ''}
                withAsterisk={index === 0}
                value={rule.duration_value || ''}
                error={lineErrors.unitValue}
                min={1}
                onChange={(value) =>
                  handleChangeDurationValue(index, Number(value))
                }
              />

              <SelectTypeDurationUnit
                data-testid={`input-unit-type-${index}`}
                label={
                  index === 0 || isMobile ? (
                    <Text>{t('price.period')}</Text>
                  ) : (
                    ''
                  )
                }
                value={rule.duration_unit}
                onChange={(type) =>
                  handleChangeDurationUnit(
                    index,
                    type as CoreItemPriceRuleDurationUnit
                  )
                }
              />

              <InputCurrency
                data-testid={`input-price-cents-${index}`}
                disabled={showPricesWithTaxes}
                value={amounts.totalWithoutTaxes.cents ?? 0}
                error={!showPricesWithTaxes && lineErrors.priceCents}
                label={
                  index > 0 ? (
                    ''
                  ) : (
                    <Flex gap='xs'>
                      <Text>{t('price.field_price.title')}</Text>
                      {!showPricesWithTaxes && (
                        <Tooltip label={t('price.final_price')}>
                          <Box>
                            <Icon icon={faInfoCircle} size='xs' />
                          </Box>
                        </Tooltip>
                      )}
                    </Flex>
                  )
                }
                onChange={(cents) =>
                  handleChangePriceWithoutTaxes(index, cents)
                }
              />

              <InputCurrency
                data-testid={`input-price-cents-with-taxes-${index}`}
                disabled={!showPricesWithTaxes}
                value={amounts.totalWithTaxes.cents ?? 0}
                error={showPricesWithTaxes && lineErrors.priceCents}
                defaultValue={100}
                label={
                  index > 0 ? (
                    ''
                  ) : (
                    <Flex gap='xs'>
                      <Text>{t('price.price_plus_taxes')}</Text>
                      {showPricesWithTaxes && (
                        <Tooltip label={t('price.final_price')}>
                          <Box>
                            <Icon icon={faInfoCircle} size='xs' />
                          </Box>
                        </Tooltip>
                      )}
                    </Flex>
                  )
                }
                onChange={(cents) => handleChangePriceWithTaxes(index, cents)}
              />
              <Button
                data-testid={`btn-delete-${index}`}
                variant='default'
                disabled={localPricingRules.length === 1}
                onClick={() => handleDeleteRow(index)}
                className={styles.btnDelete}
                mt={index > 0 ? 0 : 31}
              >
                <Text mr='sm'>{tActions('delete')}</Text>
                <Icon icon={faTrashCan} />
              </Button>
            </Box>
          )
        })}
      </Box>
      <Text color='red' size='sm' mb='md'>
        {errorMessage}
      </Text>
      <Button
        size='sm'
        variant='outline'
        leftSection={<Icon icon={faPlus} />}
        onClick={handleAddRow}
        data-testid='btn-add-row'
      >
        {t('price.add_row_rule')}
      </Button>
    </Box>
  )
}
